import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { createSalesOrder } from '../../../services/salesOrders';
import { fetchTaxes } from '../../../services/taxService';
import { fetchDiscounts } from '../../../services/discountService';
import { fetchSalesInstitutes } from '../../../services/salesInstitutesService';
import { fetchInventoryProducts } from '../../../services/inventoryProductsService';

const AddOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryInstituteId = searchParams.get('institute_id');

  const [formData, setFormData] = useState({
    sales_institute_id: queryInstituteId || '',
    products: [{ product_id: '', quantity: 1, price: '' }],
    tax_id: '',
    discount_id: '', // Added Discount ID
    order_date: new Date().toISOString().split('T')[0]
  });

  const [institutes, setInstitutes] = useState<
    { sales_institute_id: string; institute_name: string }[]
  >([]);
  const [productsList, setProductsList] = useState<
    { product_id: number; product_name: string; price: number }[]
  >([]);
  const [taxesList, setTaxesList] = useState<
    { tax_id: string; tax_rate: number }[]
  >([]);
  const [discountsList, setDiscountsList] = useState<
    { discount_id: string; discount_value: number }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch dropdown data
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [productsData, taxesData, discountsData, institutesData] =
          await Promise.all([
            fetchInventoryProducts(),
            fetchTaxes(),
            fetchDiscounts(),
            fetchSalesInstitutes()
          ]);
        setProductsList(productsData);
        setTaxesList(taxesData);
        setDiscountsList(discountsData); // Set Discounts Data
        setInstitutes(institutesData);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadDropdowns();
  }, []);

  // Handle field changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >
  ) => {
    setFormData(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  // Handle product selection
  const handleProductChange = (
    index: number,
    key: string,
    value: string | number
  ) => {
    setFormData(prevState => {
      const updatedProducts = [...prevState.products];
      updatedProducts[index] = { ...updatedProducts[index], [key]: value };
      return { ...prevState, products: updatedProducts };
    });
  };

  // Add or remove product rows
  const addProductRow = () => {
    setFormData(prevState => ({
      ...prevState,
      products: [
        ...prevState.products,
        { product_id: '', quantity: 1, price: '' }
      ]
    }));
  };

  const removeProductRow = (index: number) => {
    setFormData(prevState => ({
      ...prevState,
      products: prevState.products.filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!formData.sales_institute_id) {
      setError('Please select an institute before proceeding.');
      setSubmitting(false);
      return;
    }

    try {
      await createSalesOrder({
        sales_institute_id: formData.sales_institute_id,
        products: formData.products.map(({ product_id, quantity, price }) => ({
          product_id,
          quantity,
          price
        })),
        tax_id: formData.tax_id, // Only sending tax_id
        discount_id: formData.discount_id, // Only sending discount_id
        order_date: formData.order_date
      });
      alert('Order created successfully');
      navigate(
        `/sales/sales-institutes/institute-details/${formData.sales_institute_id}`
      );
    } catch (error) {
      setError('Error submitting order. Please try again.');
      console.error('Error creating order:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>Create Order</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading data...</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {!queryInstituteId && (
            <Form.Group className="mt-3">
              <Form.Label>Select Institute</Form.Label>
              <Form.Select
                name="sales_institute_id"
                value={formData.sales_institute_id}
                onChange={handleChange}
                required
                disabled={submitting}
              >
                <option value="">Select Institute</option>
                {institutes.map(institute => (
                  <option
                    key={institute.sales_institute_id}
                    value={institute.sales_institute_id}
                  >
                    {institute.institute_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <h5>Products</h5>
          {formData.products.map((product, index) => (
            <div key={index} className="mb-3">
              <Form.Group>
                <Form.Label>Product</Form.Label>
                <Form.Select
                  value={product.product_id}
                  onChange={e =>
                    handleProductChange(index, 'product_id', e.target.value)
                  }
                  required
                  disabled={submitting}
                >
                  <option value="">Select Product</option>
                  {productsList.map(p => (
                    <option key={p.product_id} value={p.product_id}>
                      {p.product_name} - ₹{p.price}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={product.quantity}
                  onChange={e =>
                    handleProductChange(
                      index,
                      'quantity',
                      Number(e.target.value)
                    )
                  }
                  required
                  disabled={submitting}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Price</Form.Label>
                <Form.Control
                  type="number"
                  value={product.price}
                  onChange={e =>
                    handleProductChange(index, 'price', Number(e.target.value))
                  }
                  required
                  disabled={submitting}
                />
              </Form.Group>

              {index > 0 && (
                <Button
                  variant="danger"
                  onClick={() => removeProductRow(index)}
                  disabled={submitting}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}

          <Button
            variant="secondary"
            onClick={addProductRow}
            disabled={submitting}
          >
            + Add Product
          </Button>

          {/* Order Date Field */}
          <Form.Group className="mt-3">
            <Form.Label>Order Date</Form.Label>
            <Form.Control
              type="date"
              name="order_date"
              value={formData.order_date}
              onChange={handleChange}
              disabled={submitting}
            />
          </Form.Group>

          <Button type="submit" className="mt-3" disabled={submitting}>
            {submitting ? 'Processing...' : 'Create Order'}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default AddOrder;
